import React, { useState, useEffect } from 'react';
import { db } from '../Firebase';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { 
  FileText, 
  Save, 
  RefreshCw, 
  Plus, 
  Trash2, 
  Percent, 
  ImageIcon, 
  ListOrdered,
  BookOpen,
  Globe2
} from 'lucide-react';
import Swal from 'sweetalert2';

const ensureTenFields = (arr) => {
  const safeArr = Array.isArray(arr) ? arr : [];
  if (safeArr.length >= 10) return safeArr;
  return [...safeArr, ...Array(10 - safeArr.length).fill("")];
};

const LoanManager = () => {
  const categories = [
    'Gold Loan',
    'Mortgage Loan',
    'Loan Against Deposit',
    'Business Loan',
    'Agriculture Allied Loan',
    'Vehicle Loan'
  ];

  const defaultValues = {
    'Gold Loan': {
      image: "https://images.unsplash.com/photo-1610375461246-83df859d849d?auto=format&fit=crop&w=1950&q=80",
      interest: "8.5",
      titleEn: "Gold Loan",
      subtitleEn: "Get instant cash liquidity against your gold ornaments with maximum safety.",
      descriptionEn: "Unlock the value of your gold without selling it. Our Gold Loan provides you with instant funds for any urgent requirement. We offer the highest value per gram with the lowest interest rates in the cooperative sector.",
      benefitsEn: [
        "Instant disbursement within 30 minutes",
        "Lowest interest rates in the market",
        "Secure storage in certified vaults",
        "Flexible repayment options (Bullet or EMI)",
        "Minimal documentation required",
        "Free insurance of your gold assets"
      ],
      eligibilityEn: [
        "Must be a member of Sangram Patsanstha",
        "Age above 18 years",
        "Owner of gold ornaments/coins (22 carat)",
        "Resident of the service area"
      ],
      documentsEn: [
        "Recent Passport Size Photographs",
        "Identity Proof (Aadhaar Card, PAN Card)",
        "Address Proof",
        "Gold Purity Certification (Done at branch)"
      ],
      titleMr: "सोने कर्ज",
      subtitleMr: "अधिकतम सुरक्षिततेसह तुमच्या सोन्याच्या दागिन्यांवर त्वरित रोख रक्कम मिळवा.",
      descriptionMr: "तुमचे सोने न विकता त्याचे मूल्य अनलॉक करा. आमचे सोने कर्ज तुम्हाला कोणत्याही तातडीच्या गरजेसाठी त्वरित निधी प्रदान करते. आम्ही सहकार क्षेत्रात सर्वात कमी व्याजदरासह प्रति ग्रॅम सर्वोच्च मूल्यऑफर करतो.",
      benefitsMr: [
        "३० मिनिटांत त्वरित कर्ज वितरण",
        "बाजारातील सर्वात कमी व्याजदर",
        "प्रमाणित तिजोऱ्यांमध्ये सुरक्षित साठवणूक",
        "लवचिक परतफेड पर्याय (बुलेट किंवा ईएमआय)",
        "कमीत कमी कागदपत्रांची आवश्यकता",
        "तुमच्या सोन्याच्या मालमत्तेचा मोफत विमा"
      ],
      eligibilityMr: [
        "संग्राम पतसंस्थेचे सभासद असणे आवश्यक आहे",
        "वय १८ वर्षांपेक्षा जास्त असावे",
        "सोन्याचे दागिने/नाणी (२२ कॅरेट) चे मालक असावेत",
        "संबंधित कार्यक्षेत्राचे रहिवासी असावेत"
      ],
      documentsMr: [
        "अलीकडील पासपोर्ट आकाराचे छायाचित्र",
        "ओळख पुरावा (आधार कार्ड, पॅन कार्ड)",
        "पत्ता पुरावा",
        "सोन्याच्या शुद्धतेचे प्रमाणपत्र (शाखेत केले जाते)"
      ]
    },
    'Mortgage Loan': {
      image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80",
      interest: "10.0",
      titleEn: "Mortgage Loan",
      subtitleEn: "Leverage your property's value for your long-term financial goals.",
      descriptionEn: "Our Mortgage Loan allows you to fulfill your large financial requirements by leveraging your residential or commercial property. Whether it's for business expansion, child's higher education, or debt consolidation, we offer flexible terms that suit your needs.",
      benefitsEn: [
        "High loan-to-value ratio",
        "Long repayment tenure up to 15 years",
        "Lower interest rates compared to unsecured loans",
        "Available for both residential and commercial properties",
        "Simple and transparent legal processing",
        "No prepayment charges after 1 year"
      ],
      eligibilityEn: [
        "Must be a member of Sangram Patsanstha",
        "Property must be within our service area",
        "Stable income to cover EMI payments",
        "Clear title and ownership of property",
        "Age between 21 and 65 years"
      ],
      documentsEn: [
        "Original Property Documents (Sale Deed, Title Search)",
        "Approved Building Plan",
        "Tax Receipts for the last 3 years",
        "Income Proof and Bank Statements",
        "KYC Documents of all owners"
      ],
      titleMr: "तारण कर्ज",
      subtitleMr: "तुमच्या दीर्घकालीन आर्थिक उद्दिष्टांसाठी तुमच्या मालमत्तेच्या मूल्याचा वापर करा.",
      descriptionMr: "आमचे तारण कर्ज तुम्हाला तुमच्या निवासी किंवा व्यावसायिक मालमत्तेचा वापर करून तुमच्या मोठ्या आर्थिक गरजा पूर्ण करण्यास मदत करते. मग ते व्यवसाय विस्तार, मुलांचे उच्च शिक्षण किंवा कर्ज एकत्रीकरणासाठी असो, आम्ही तुमच्या गरजांनुसार लवचिक अटी ऑफर करतो.",
      benefitsMr: [
        "उच्च कर्ज-ते-मूल्य (LTV) प्रमाण",
        "१५ वर्षांपर्यंतचा दीर्घ परतफेड कालावधी",
        "असुरक्षित कर्जाच्या तुलनेत कमी व्याजदर",
        "निवासी आणि व्यावसायिक अशा दोन्ही मालमत्तांवर उपलब्ध",
        "साधी आणि पारदर्शक कायदेशीर प्रक्रिया",
        "१ वर्षानंतर कोणतेही मुदतपूर्व परतफेड शुल्क नाही"
      ],
      eligibilityMr: [
        "संग्राम पतसंस्थेचे सभासद असणे आवश्यक आहे",
        "मालमत्ता आमच्या कार्यक्षेत्रात असणे आवश्यक आहे",
        "हप्ते (EMI) भरण्यासाठी स्थिर उत्पन्नाचा स्रोत असावा",
        "मालमत्तेचे स्पष्ट शीर्षक (Title) and मालकी असावी",
        "वय २१ ते ६५ वर्षांच्या दरम्यान असावे"
      ],
      documentsMr: [
        "मूळ मालमत्ता दस्तऐवज (विक्री करार, टायटल सर्च)",
        "मंजूर इमारत नकाशा (Building Plan)",
        "मागील ३ वर्षांच्या मालमत्ता कर पावत्या",
        "उत्पन्नाचा पुरावा आणि बँक खाते विवरण",
        "सर्व मालमत्ता मालकांची केवायसी (KYC) कागदपत्रे"
      ]
    },
    'Loan Against Deposit': {
      image: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80",
      interest: "9.5",
      titleEn: "Loan Against Deposit",
      subtitleEn: "Get quick and low-interest loans secured by your fixed or recurring deposits.",
      descriptionEn: "Avail quick loans against your fixed deposits (FD) or recurring deposits (RD) held with Sangram Patsanstha. Keep your savings intact while meeting your immediate financial needs at extremely competitive rates.",
      benefitsEn: [
        "Up to 90% loan against your deposit amount",
        "Super-fast processing with minimal paperwork",
        "Lower interest rate (usually 1% to 2% above the deposit interest rate)",
        "No pre-payment penalty or processing charges",
        "Keep earning interest on your underlying deposits",
        "Flexible repayment options"
      ],
      eligibilityEn: [
        "Must hold an active Fixed Deposit (FD) or Recurring Deposit (RD) with Sangram Patsanstha",
        "Must be a member of Sangram Patsanstha",
        "Resident of the operational area",
        "Age above 18 years"
      ],
      documentsEn: [
        "Original Deposit Receipt / Passbook (FD/RD Receipt)",
        "Signed Loan Application and Agreement form",
        "Lien marking authorization form",
        "KYC Documents (Aadhaar Card, PAN Card)",
        "Recent passport-size photographs of the depositor(s)"
      ],
      titleMr: "ठेव तारण कर्ज",
      subtitleMr: "तुमच्या मुदत किंवा आवर्ती ठेवींच्या बदल्यात जलद आणि कमी व्याजाचे कर्ज मिळवा.",
      descriptionMr: "संग्राम पतसंस्थेकडे असलेल्या तुमच्या मुदत ठेवी (FD) किंवा आवर्ती ठेवी (RD) च्या बदल्यात जलद कर्ज मिळवा. अत्यंत स्पर्धात्मक दरांवर तुमच्या तात्काळ आर्थिक गरजा पूर्ण करताना तुमची बचत सुरक्षित ठेवा.",
      benefitsMr: [
        "तुमच्या ठेव रकमेच्या ९०% पर्यंत कर्ज मिळवा",
        "कमीत कमी कागदपत्रांसह अतिशय जलद कर्ज मंजुरी",
        "कमी व्याजदर (साधारणपणे तुमच्या ठेवीवरील व्याजदरापेक्षा फक्त १% ते २% जास्त)",
        "कोणतेही मुदतपूर्व परतफेड शुल्क किंवा प्रक्रिया शुल्क नाही",
        "कर्ज सुरू असतानाही मूळ ठेवींवर व्याज मिळवणे सुरू ठेवा",
        "लवचिक परतफेडीचे पर्याय"
      ],
      eligibilityMr: [
        "संग्राम पतसंस्थेत मुदत ठेव (FD) किंवा आवर्ती ठेव (RD) खाते असणे आवश्यक आहे",
        "संग्राम पतसंस्थेचे सभासद असणे आवश्यक आहे",
        "संबंधित कार्यक्षेत्रातील रहिवासी असावेत",
        "वय १८ वर्षांपेक्षा जास्त असावे"
      ],
      documentsMr: [
        "मूळ ठेव पावती / पासबुक (FD/RD पावती)",
        "स्वाक्षरी केलेले कर्ज अर्ज आणि करारपत्र",
        "लियन मार्किंग (तारण नोंदणी) संमती पत्र",
        "केवायसी (KYC) कागदपत्रे (आधार कार्ड, पॅन कार्ड)",
        "ठेवधारकांचे अलीकडील पासपोर्ट आकाराचे छायाचित्र"
      ]
    },
    'Business Loan': {
      image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80",
      interest: "11.5",
      titleEn: "Business Loan",
      subtitleEn: "Fueling the growth of small and medium enterprises in our community.",
      descriptionEn: "We understand that every business has unique needs. Our Business Loan is specifically designed for small businesses, shopkeepers, and entrepreneurs to help them expand their operations, purchase inventory, or upgrade machinery. We are your partners in growth.",
      benefitsEn: [
        "Flexible working capital solutions",
        "Term loans for machinery and equipment",
        "Fast processing for time-sensitive opportunities",
        "Doorstep banking service for businessmen",
        "Special rates for women entrepreneurs",
        "No collateral required for small amounts"
      ],
      eligibilityEn: [
        "Must be a member of Sangram Patsanstha",
        "Business must be operational for at least 2 years",
        "Valid business registration and licenses",
        "Minimum annual turnover of ₹10,00,000",
        "Satisfactory credit history"
      ],
      documentsEn: [
        "Business Registration Certificate / Shop Act License",
        "ITR for the last 2 years",
        "GSTR reports (if applicable)",
        "Bank Statement of the business account",
        "KYC of Proprietor / Partners"
      ],
      titleMr: "व्यावसायिक कर्ज",
      subtitleMr: "आमच्या समुदायातील लहान आणि मध्यम उद्योगांच्या वाढीला चालना देणे.",
      descriptionMr: "आम्ही समजतो की प्रत्येक व्यवसायाच्या गरजा अनोख्या असतात. आमचे व्यावसायिक कर्ज विशेषतः लहान व्यावसायिक, दुकानदार आणि उद्योजकांसाठी डिझाइन केलेले आहे जेणेकरून त्यांना त्यांचा व्यवसाय वाढवण्यासाठी, नवीन माल खरेदी करण्यासाठी किंवा यंत्रसामग्री अद्ययावत करण्यासाठी मदत होईल. आम्ही तुमच्या प्रगतीचे भागीदार आहोत.",
      benefitsMr: [
        "लवचिक खेळते भांडवल पर्याय",
        "यंत्रसामग्री आणि उपकरणांसाठी मुदत कर्ज",
        "वेळेचे महत्त्व असलेल्या संधींसाठी जलद प्रक्रिया",
        "व्यावसायिकांसाठी घरपोच बँकिंग सेवा",
        "महिला उद्योजिकांसाठी विशेष व्याजदर सवलत",
        "लहान रकमेसाठी कोणत्याही तारणाची आवश्यकता नाही"
      ],
      eligibilityMr: [
        "संग्राम पतसंस्थेचे सभासद असणे आवश्यक आहे",
        "व्यवसाय किमान २ वर्षांपासून कार्यरत असावा",
        "वैध व्यवसाय नोंदणी आणि आवश्यक परवाने असावेत",
        "किमान वार्षिक उलाढाल (Turnover) ₹१०,००,००० असावी",
        "समाधानकारक क्रेडिट इतिहास (Credit History) असावा"
      ],
      documentsMr: [
        "व्यवसाय नोंदणी प्रमाणपत्र / शॉप ऍक्ट लायसन्स (Shop Act)",
        "मागील २ वर्षांचा आयकर परतावा (ITR)",
        "जीएसटीआर (GSTR) अहवाल (लागू असल्यास)",
        "व्यावसायिक बँक खात्याचे विवरण (Statement)",
        "मालक / भागीदारांची केवायसी (KYC) कागदपत्रे"
      ]
    },
    'Agriculture Allied Loan': {
      image: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80",
      interest: "7.0",
      titleEn: "Agriculture Allied Loan",
      subtitleEn: "Dedicated financial support for our farmers and rural development.",
      descriptionEn: "Farmers are the backbone of our economy. Our Agriculture Allied Loan is designed to support various farming activities, from purchasing seeds and fertilizers to investing in modern irrigation systems and tractors. We offer terms that align with your harvest cycles.",
      benefitsEn: [
        "Lowest interest rates in the cooperative sector",
        "Repayment schedules linked to harvest seasons",
        "No processing fees for small-scale farmers",
        "Support for allied activities like dairy and poultry",
        "Special discounts on crop insurance premiums",
        "Doorstep assistance at villages"
      ],
      eligibilityEn: [
        "Must be a member of Sangram Patsanstha",
        "Ownership of agricultural land (7/12 extract)",
        "Resident of the rural service area",
        "Good standing in the local farming community"
      ],
      documentsEn: [
        "Recent 7/12 and 8A extracts of land",
        "No Objection Certificate (NOC) from other banks",
        "Aadhaar Card and PAN Card",
        "Land valuation report (if required)",
        "Passport size photographs"
      ],
      titleMr: "कृषी संलग्न कर्ज",
      subtitleMr: "आमच्या शेतकऱ्यांसाठी आणि ग्रामीण विकासासाठी समर्पित आर्थिक सहाय्य.",
      descriptionMr: "शेतकरी आपल्या अर्थव्यवस्थेचा कणा आहेत. आमचे कृषी संलग्न कर्ज बियाणे आणि खते खरेदी करण्यापासून ते आधुनिक सिंचन यंत्रणा आणि ट्रॅक्टरमध्ये गुंतवणूक करण्यापर्यंत विविध शेती उपक्रमांना पाठबळ देण्यासाठी डिझाइन केलेले आहे. आम्ही तुमच्या पिकांच्या कापणी चक्राशी जुळणारे परतफेडीचे पर्याय देतो.",
      benefitsMr: [
        "सहकार क्षेत्रातील सर्वात कमी व्याजदर",
        "पिकांच्या कापणीच्या हंगामाशी जोडलेले परतफेड वेळापत्रक",
        "लहान शेतकऱ्यांसाठी कोणतेही प्रक्रिया शुल्क नाही",
        "दुग्धव्यवसाय आणि कुक्कुटपालनासारख्या पूरक उपक्रमांना मदत",
        "पीक विमा प्रीमियमवर विशेष सवलत",
        "गावोगावी जाऊन घरपोच साहाय्य"
      ],
      eligibilityMr: [
        "संग्राम पतसंस्थेचे सभासद असणे आवश्यक आहे",
        "शेती जमिनीची मालकी (७/१२ उतारा) असणे आवश्यक आहे",
        "ग्रामीण कार्यक्षेत्राचे रहिवासी असावेत",
        "स्थानिक शेतकरी समुदायात चांगली प्रतिष्ठा असावी"
      ],
      documentsMr: [
        "जमिनीचा अलीकडील ७/१२ (सातबारा) आणि ८अ उतारा",
        "इतर बँकांकडून कोणतेही कर्ज थकीत नसल्याचे प्रमाणपत्र (NOC)",
        "आधार कार्ड आणि पॅन कार्ड",
        "जमिनीचा मूल्यांकन अहवाल (आवश्यक असल्यास)",
        "पासपोर्ट आकाराचे छायाचित्र"
      ]
    },
    'Vehicle Loan': {
      image: "https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&w=1950&q=80",
      interest: "9.5",
      titleEn: "Vehicle Loan",
      subtitleEn: "Drive your dream vehicle home with our easy and quick vehicle financing options.",
      descriptionEn: "Purchase a new or pre-owned car, two-wheeler, or commercial vehicle with our quick and hassle-free Vehicle Loans. We offer flexible repayment plans, minimal documentation, and attractive interest rates to make your vehicle purchase smooth and simple.",
      benefitsEn: [
        "Up to 85% to 90% financing of the vehicle's road price",
        "Quick approval and documentation within 24-48 hours",
        "Flexible repayment tenure up to 5-7 years",
        "Attractive interest rates with no hidden costs",
        "Easy monthly repayment options (EMI)",
        "Available for both personal and commercial vehicles"
      ],
      eligibilityEn: [
        "Must be a member of Sangram Patsanstha",
        "Age between 21 and 65 years",
        "Steady income source (salaried or self-employed)",
        "Resident within the operational area of our branch"
      ],
      documentsEn: [
        "KYC Documents (Aadhaar Card, PAN Card)",
        "Recent Passport Size Photographs",
        "Address Proof",
        "Income Proof (Salary slips or ITR for last 2 years)",
        "Bank Account Statement for the last 6 months",
        "Proforma Invoice / Quotation of the vehicle to be purchased"
      ],
      titleMr: "वाहन कर्ज",
      subtitleMr: "आमच्या सुलभ आणि जलद वाहन कर्ज पर्यायांसह तुमचे स्वप्नातील वाहन घरी आणा.",
      descriptionMr: "आमच्या जलद आणि सुलभ वाहन कर्जाद्वारे नवीन किंवा जुनी कार, दुचाकी किंवा व्यावसायिक वाहन खरेदी करा. आम्ही तुमच्या वाहन खरेदीला सोपे करण्यासाठी लवचिक परतफेड योजना, कमीत कमी कागदपत्रे आणि आकर्षक व्याजदर ऑफर करतो.",
      benefitsMr: [
        "वाहनाच्या ऑन-रोड किमतीच्या ८५% ते ९०% पर्यंत कर्ज पुरवठा",
        "२४ ते ४८ तासांत जलद मंजुरी आणि कागदपत्रे प्रक्रिया",
        "५ ते ७ वर्षांपर्यंतचा लवचिक परतफेड कालावधी",
        "कोणत्याही छुपा शुल्काशिवाय आकर्षक व्याजदर",
        "सुलभ मासिक हप्ते (EMI) पर्याय",
        "वैयक्तिक आणि व्यावसायिक दोन्ही वाहनांसाठी उपलब्ध"
      ],
      eligibilityMr: [
        "संग्राम पतसंस्थेचे सभासद असणे आवश्यक आहे",
        "वय २१ ते ६५ वर्षांच्या दरम्यान असावे",
        "उत्पन्नाचा नियमित स्रोत (पगारदार किंवा स्वयंरोजगार) असावा",
        "शाखेच्या कार्यक्षेत्रातील रहिवासी असावेत"
      ],
      documentsMr: [
        "केवायसी (KYC) कागदपत्रे (आधार कार्ड, पॅन कार्ड)",
        "अलीकडील पासपोर्ट आकाराचे छायाचित्र",
        "पत्ता पुरावा",
        "उत्पन्नाचा पुरावा (पगार पत्रक किंवा मागील २ वर्षांचा आयटीआर)",
        "मागील ६ महिन्यांचे बँक खाते विवरण (Statement)",
        "खरेदी करावयाच्या वाहनाचे प्रोफॉर्मा इनव्हॉइस / कोटेशन"
      ]
    }
  };

  const [activeTab, setActiveTab] = useState('Gold Loan');
  const [langTab, setLangTab] = useState('en'); // 'en' or 'mr'
  const [loans, setLoans] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load data for all categories
  useEffect(() => {
    const fetchAllLoans = async () => {
      const dbLoans = {};
      
      // We will listen/setup listeners for all loan documents
      const unsubscribers = categories.map(cat => {
        return onSnapshot(doc(db, 'loans', cat), (docSnap) => {
          if (docSnap.exists()) {
            dbLoans[cat] = docSnap.data();
          } else {
            // Load from default static values
            dbLoans[cat] = defaultValues[cat];
          }

          // If all categories are resolved (or we update as we go)
          const rawData = {
            ...defaultValues[cat],
            ...dbLoans[cat]
          };

          rawData.loanFileOrderEn = ensureTenFields(rawData.loanFileOrderEn);
          rawData.loanFileOrderMr = ensureTenFields(rawData.loanFileOrderMr);
          rawData.guarantorDocsEn = ensureTenFields(rawData.guarantorDocsEn);
          rawData.guarantorDocsMr = ensureTenFields(rawData.guarantorDocsMr);
          rawData.mediumTermDocsEn = ensureTenFields(rawData.mediumTermDocsEn);
          rawData.mediumTermDocsMr = ensureTenFields(rawData.mediumTermDocsMr);
          rawData.siteInspectionDocsEn = ensureTenFields(rawData.siteInspectionDocsEn);
          rawData.siteInspectionDocsMr = ensureTenFields(rawData.siteInspectionDocsMr);

          setLoans(prev => ({
            ...prev,
            [cat]: rawData
          }));
          
          if (Object.keys(dbLoans).length === categories.length) {
            setLoading(false);
          }
        });
      });

      // Simple safety fallback loading resolver in case onSnapshot takes time
      const timeout = setTimeout(() => {
        setLoading(false);
      }, 2000);

      return () => {
        unsubscribers.forEach(unsub => unsub());
        clearTimeout(timeout);
      };
    };

    fetchAllLoans();
  }, []);

  const handleFieldChange = (category, field, value) => {
    setLoans(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  // Generic List item editing functions
  const handleListItemChange = (category, field, index, value) => {
    setLoans(prev => {
      const currentList = [...(prev[category]?.[field] || [])];
      currentList[index] = value;
      return {
        ...prev,
        [category]: {
          ...prev[category],
          [field]: currentList
        }
      };
    });
  };

  const addListItem = (category, field) => {
    setLoans(prev => {
      const currentList = [...(prev[category]?.[field] || [])];
      currentList.push("");
      return {
        ...prev,
        [category]: {
          ...prev[category],
          [field]: currentList
        }
      };
    });
  };

  const removeListItem = (category, field, index) => {
    setLoans(prev => {
      const currentList = [...(prev[category]?.[field] || [])];
      currentList.splice(index, 1);
      return {
        ...prev,
        [category]: {
          ...prev[category],
          [field]: currentList
        }
      };
    });
  };

  const handleSave = async (category) => {
    const loanData = loans[category];
    if (!loanData) return;

    setSaving(true);
    Swal.showLoading();

    try {
      // 1. Save full loan details to `loans` collection
      await setDoc(doc(db, 'loans', category), {
        ...loanData,
        updatedAt: new Date()
      }, { merge: true });

      // 2. Also save interest rate to `loan_interests` collection for backward compatibility
      await setDoc(doc(db, 'loan_interests', category), {
        rate: loanData.interest || "0.0",
        updatedAt: new Date()
      }, { merge: true });

      Swal.fire({
        icon: 'success',
        title: 'Successfully Saved!',
        text: `${category} data has been updated and reflected.`,
        timer: 2000,
        showConfirmButton: false,
        confirmButtonColor: '#0D47A1'
      });
    } catch (error) {
      console.error("Error saving loan details:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error Saving Data',
        text: 'Failed to update database. Please try again.',
        confirmButtonColor: '#0D47A1'
      });
    } finally {
      setSaving(false);
    }
  };

  const currentLoan = loans[activeTab] || defaultValues[activeTab];

  return (
    <div className="space-y-8 font-poppins">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-extrabold text-primary-dark">Manage Loans</h1>
          <p className="text-gray-500 font-medium">Dynamically update loan details, fields, key benefits, and interest rates for user pages.</p>
        </div>
      </div>

      {/* Main Container */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 md:p-10">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 space-y-4">
            <RefreshCw className="w-12 h-12 text-primary animate-spin" />
            <span className="text-gray-500 font-medium">Fetching loan configurations...</span>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2 border-b border-gray-100 pb-6">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveTab(cat)}
                  className={`px-6 py-3.5 rounded-2xl font-bold text-sm transition-all duration-300 ${
                    activeTab === cat
                      ? 'bg-primary text-white shadow-lg shadow-primary/20 scale-102'
                      : 'bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-primary-dark'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Editing Form for Current Category */}
            <div className="grid lg:grid-cols-3 gap-10">
              
              {/* Left Form: Common & Media Options */}
              <div className="lg:col-span-1 space-y-6">
                <div className="p-6 bg-gray-50 rounded-3xl border border-gray-100 space-y-6">
                  <h3 className="font-extrabold text-lg text-primary-dark flex items-center gap-2">
                    <FileText className="w-5 h-5 text-secondary" />
                    General Settings
                  </h3>

                  {/* Interest Rate */}
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-widest flex items-center gap-1.5">
                      <Percent className="w-4 h-4 text-gray-400" />
                      Starting Interest Rate (%)
                    </label>
                    <input
                      type="number"
                      step="0.05"
                      value={currentLoan?.interest || ''}
                      onChange={(e) => handleFieldChange(activeTab, 'interest', e.target.value)}
                      className="w-full bg-white border border-gray-200 px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/25 font-bold text-gray-800"
                      placeholder="e.g. 10.5"
                    />
                  </div>

                  {/* Image URL */}
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-widest flex items-center gap-1.5">
                      <ImageIcon className="w-4 h-4 text-gray-400" />
                      Banner Image URL
                    </label>
                    <input
                      type="text"
                      value={currentLoan?.image || ''}
                      onChange={(e) => handleFieldChange(activeTab, 'image', e.target.value)}
                      className="w-full bg-white border border-gray-200 px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/25 font-medium text-gray-600 text-sm"
                      placeholder="Image URL"
                    />
                  </div>

                  {/* Image Preview */}
                  {currentLoan?.image && (
                    <div className="relative pt-[56.25%] rounded-2xl overflow-hidden border border-gray-200 shadow-inner bg-gray-100">
                      <img
                        src={currentLoan.image}
                        alt="Banner Preview"
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = "https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80";
                        }}
                      />
                    </div>
                  )}

                  {/* Save Button */}
                  <button
                    onClick={() => handleSave(activeTab)}
                    disabled={saving}
                    className="w-full btn-primary flex items-center justify-center gap-2 py-4 rounded-xl shadow-lg shadow-primary/30 font-bold"
                  >
                    {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                    Save {activeTab}
                  </button>
                </div>
              </div>

              {/* Right Form: Dynamic Translations Tabs */}
              <div className="lg:col-span-2 space-y-6">
                <div className="flex border-b border-gray-100">
                  <button
                    onClick={() => setLangTab('en')}
                    className={`flex items-center gap-2 px-6 py-3 font-bold text-sm border-b-2 transition-all ${
                      langTab === 'en'
                        ? 'border-secondary text-primary font-bold'
                        : 'border-transparent text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    <Globe2 className="w-4 h-4" />
                    English Fields
                  </button>
                  <button
                    onClick={() => setLangTab('mr')}
                    className={`flex items-center gap-2 px-6 py-3 font-bold text-sm border-b-2 transition-all ${
                      langTab === 'mr'
                        ? 'border-secondary text-primary font-bold'
                        : 'border-transparent text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    <Globe2 className="w-4 h-4" />
                    Marathi (मराठी) Fields
                  </button>
                </div>

                {langTab === 'en' ? (
                  // ENGLISH FIELDS
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Loan Title (English)</label>
                        <input
                          type="text"
                          value={currentLoan?.titleEn || ''}
                          onChange={(e) => handleFieldChange(activeTab, 'titleEn', e.target.value)}
                          className="w-full bg-gray-50 border border-gray-200 px-4 py-3 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25 font-bold text-gray-800"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Sub-Title (English)</label>
                        <input
                          type="text"
                          value={currentLoan?.subtitleEn || ''}
                          onChange={(e) => handleFieldChange(activeTab, 'subtitleEn', e.target.value)}
                          className="w-full bg-gray-50 border border-gray-200 px-4 py-3 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25 font-medium text-gray-700"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-widest flex items-center gap-1.5">
                        <BookOpen className="w-4 h-4 text-gray-400" />
                        Overview Description (English)
                      </label>
                      <textarea
                        rows={4}
                        value={currentLoan?.descriptionEn || ''}
                        onChange={(e) => handleFieldChange(activeTab, 'descriptionEn', e.target.value)}
                        className="w-full bg-gray-50 border border-gray-200 px-4 py-3 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25 font-medium text-gray-700 leading-relaxed text-sm"
                      />
                    </div>

                    {/* Bullet Points Management: Benefits, Eligibility, Documents */}
                    <div className="space-y-8 border-t border-gray-100 pt-8">
                      {/* Benefits */}
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-extrabold text-base text-primary-dark flex items-center gap-2">
                            <ListOrdered className="w-5 h-5 text-secondary" />
                            Key Benefits (English)
                          </h4>
                          <button
                            onClick={() => addListItem(activeTab, 'benefitsEn')}
                            className="flex items-center gap-1 text-xs font-bold text-secondary hover:text-secondary-dark transition-colors px-3 py-1.5 bg-secondary/5 hover:bg-secondary/10 rounded-xl"
                          >
                            <Plus className="w-4 h-4" /> Add Benefit
                          </button>
                        </div>
                        <div className="space-y-3">
                          {currentLoan?.benefitsEn?.map((benefit, i) => (
                            <div key={i} className="flex gap-3 items-center">
                              <span className="w-6 h-6 rounded-full bg-emerald-50 text-emerald-600 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                {i + 1}
                              </span>
                              <input
                                type="text"
                                value={benefit}
                                onChange={(e) => handleListItemChange(activeTab, 'benefitsEn', i, e.target.value)}
                                className="w-full bg-gray-50 border border-gray-200 px-4 py-2.5 rounded-xl focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary/20 text-sm font-medium text-gray-700"
                              />
                              <button
                                onClick={() => removeListItem(activeTab, 'benefitsEn', i)}
                                className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                              >
                                <Trash2 className="w-4.5 h-4.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Eligibility */}
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-extrabold text-base text-primary-dark flex items-center gap-2">
                            <ListOrdered className="w-5 h-5 text-secondary" />
                            Eligibility Criteria (English)
                          </h4>
                          <button
                            onClick={() => addListItem(activeTab, 'eligibilityEn')}
                            className="flex items-center gap-1 text-xs font-bold text-secondary hover:text-secondary-dark transition-colors px-3 py-1.5 bg-secondary/5 hover:bg-secondary/10 rounded-xl"
                          >
                            <Plus className="w-4 h-4" /> Add Criteria
                          </button>
                        </div>
                        <div className="space-y-3">
                          {currentLoan?.eligibilityEn?.map((item, i) => (
                            <div key={i} className="flex gap-3 items-center">
                              <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                {i + 1}
                              </span>
                              <input
                                type="text"
                                value={item}
                                onChange={(e) => handleListItemChange(activeTab, 'eligibilityEn', i, e.target.value)}
                                className="w-full bg-gray-50 border border-gray-200 px-4 py-2.5 rounded-xl focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary/20 text-sm font-medium text-gray-700"
                              />
                              <button
                                onClick={() => removeListItem(activeTab, 'eligibilityEn', i)}
                                className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                              >
                                <Trash2 className="w-4.5 h-4.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Loan File Order */}
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-extrabold text-base text-primary-dark flex items-center gap-2">
                            <ListOrdered className="w-5 h-5 text-secondary" />
                            Loan File Documents Serial Order (English)
                          </h4>
                          <button
                            onClick={() => addListItem(activeTab, 'loanFileOrderEn')}
                            className="flex items-center gap-1 text-xs font-bold text-secondary hover:text-secondary-dark transition-colors px-3 py-1.5 bg-secondary/5 hover:bg-secondary/10 rounded-xl"
                          >
                            <Plus className="w-4 h-4" /> Add Document
                          </button>
                        </div>
                        <div className="space-y-3">
                          {currentLoan?.loanFileOrderEn?.map((item, i) => (
                            <div key={i} className="flex gap-3 items-center">
                              <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                {i + 1}
                              </span>
                              <input
                                type="text"
                                value={item}
                                onChange={(e) => handleListItemChange(activeTab, 'loanFileOrderEn', i, e.target.value)}
                                className="w-full bg-gray-50 border border-gray-200 px-4 py-2.5 rounded-xl focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary/20 text-sm font-medium text-gray-700"
                              />
                              <button
                                onClick={() => removeListItem(activeTab, 'loanFileOrderEn', i)}
                                className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                              >
                                <Trash2 className="w-4.5 h-4.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Guarantor Documents */}
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-extrabold text-base text-primary-dark flex items-center gap-2">
                            <ListOrdered className="w-5 h-5 text-secondary" />
                            Guarantor Documents (English)
                          </h4>
                          <button
                            onClick={() => addListItem(activeTab, 'guarantorDocsEn')}
                            className="flex items-center gap-1 text-xs font-bold text-secondary hover:text-secondary-dark transition-colors px-3 py-1.5 bg-secondary/5 hover:bg-secondary/10 rounded-xl"
                          >
                            <Plus className="w-4 h-4" /> Add Document
                          </button>
                        </div>
                        <div className="space-y-3">
                          {currentLoan?.guarantorDocsEn?.map((item, i) => (
                            <div key={i} className="flex gap-3 items-center">
                              <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                {i + 1}
                              </span>
                              <input
                                type="text"
                                value={item}
                                onChange={(e) => handleListItemChange(activeTab, 'guarantorDocsEn', i, e.target.value)}
                                className="w-full bg-gray-50 border border-gray-200 px-4 py-2.5 rounded-xl focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary/20 text-sm font-medium text-gray-700"
                              />
                              <button
                                onClick={() => removeListItem(activeTab, 'guarantorDocsEn', i)}
                                className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                              >
                                <Trash2 className="w-4.5 h-4.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Medium Term Docs */}
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-extrabold text-base text-primary-dark flex items-center gap-2">
                            <ListOrdered className="w-5 h-5 text-secondary" />
                            Documents Required for Medium Term Loan (English)
                          </h4>
                          <button
                            onClick={() => addListItem(activeTab, 'mediumTermDocsEn')}
                            className="flex items-center gap-1 text-xs font-bold text-secondary hover:text-secondary-dark transition-colors px-3 py-1.5 bg-secondary/5 hover:bg-secondary/10 rounded-xl"
                          >
                            <Plus className="w-4 h-4" /> Add Document
                          </button>
                        </div>
                        <div className="space-y-3">
                          {currentLoan?.mediumTermDocsEn?.map((item, i) => (
                            <div key={i} className="flex gap-3 items-center">
                              <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                {i + 1}
                              </span>
                              <input
                                type="text"
                                value={item}
                                onChange={(e) => handleListItemChange(activeTab, 'mediumTermDocsEn', i, e.target.value)}
                                className="w-full bg-gray-50 border border-gray-200 px-4 py-2.5 rounded-xl focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary/20 text-sm font-medium text-gray-700"
                              />
                              <button
                                onClick={() => removeListItem(activeTab, 'mediumTermDocsEn', i)}
                                className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                              >
                                <Trash2 className="w-4.5 h-4.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Site Inspection Report */}
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-extrabold text-base text-primary-dark flex items-center gap-2">
                            <ListOrdered className="w-5 h-5 text-secondary" />
                            Branch Officer Site Inspection Report (English)
                          </h4>
                          <button
                            onClick={() => addListItem(activeTab, 'siteInspectionDocsEn')}
                            className="flex items-center gap-1 text-xs font-bold text-secondary hover:text-secondary-dark transition-colors px-3 py-1.5 bg-secondary/5 hover:bg-secondary/10 rounded-xl"
                          >
                            <Plus className="w-4 h-4" /> Add Document
                          </button>
                        </div>
                        <div className="space-y-3">
                          {currentLoan?.siteInspectionDocsEn?.map((item, i) => (
                            <div key={i} className="flex gap-3 items-center">
                              <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                {i + 1}
                              </span>
                              <input
                                type="text"
                                value={item}
                                onChange={(e) => handleListItemChange(activeTab, 'siteInspectionDocsEn', i, e.target.value)}
                                className="w-full bg-gray-50 border border-gray-200 px-4 py-2.5 rounded-xl focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary/20 text-sm font-medium text-gray-700"
                              />
                              <button
                                onClick={() => removeListItem(activeTab, 'siteInspectionDocsEn', i)}
                                className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                              >
                                <Trash2 className="w-4.5 h-4.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Documents Required */}
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-extrabold text-base text-primary-dark flex items-center gap-2">
                            <ListOrdered className="w-5 h-5 text-secondary" />
                            Documents Required (English)
                          </h4>
                          <button
                            onClick={() => addListItem(activeTab, 'documentsEn')}
                            className="flex items-center gap-1 text-xs font-bold text-secondary hover:text-secondary-dark transition-colors px-3 py-1.5 bg-secondary/5 hover:bg-secondary/10 rounded-xl"
                          >
                            <Plus className="w-4 h-4" /> Add Document
                          </button>
                        </div>
                        <div className="space-y-3">
                          {currentLoan?.documentsEn?.map((docItem, i) => (
                            <div key={i} className="flex gap-3 items-center">
                              <span className="w-6 h-6 rounded-full bg-purple-50 text-purple-600 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                {i + 1}
                              </span>
                              <input
                                type="text"
                                value={docItem}
                                onChange={(e) => handleListItemChange(activeTab, 'documentsEn', i, e.target.value)}
                                className="w-full bg-gray-50 border border-gray-200 px-4 py-2.5 rounded-xl focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary/20 text-sm font-medium text-gray-700"
                              />
                              <button
                                onClick={() => removeListItem(activeTab, 'documentsEn', i)}
                                className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                              >
                                <Trash2 className="w-4.5 h-4.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  // MARATHI FIELDS
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Loan Title (Marathi)</label>
                        <input
                          type="text"
                          value={currentLoan?.titleMr || ''}
                          onChange={(e) => handleFieldChange(activeTab, 'titleMr', e.target.value)}
                          className="w-full bg-gray-50 border border-gray-200 px-4 py-3 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25 font-bold text-gray-800"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Sub-Title (Marathi)</label>
                        <input
                          type="text"
                          value={currentLoan?.subtitleMr || ''}
                          onChange={(e) => handleFieldChange(activeTab, 'subtitleMr', e.target.value)}
                          className="w-full bg-gray-50 border border-gray-200 px-4 py-3 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25 font-medium text-gray-700"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-widest flex items-center gap-1.5">
                        <BookOpen className="w-4 h-4 text-gray-400" />
                        Overview Description (Marathi)
                      </label>
                      <textarea
                        rows={4}
                        value={currentLoan?.descriptionMr || ''}
                        onChange={(e) => handleFieldChange(activeTab, 'descriptionMr', e.target.value)}
                        className="w-full bg-gray-50 border border-gray-200 px-4 py-3 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25 font-medium text-gray-700 leading-relaxed text-sm"
                      />
                    </div>

                    {/* Bullet Points Management: Benefits, Eligibility, Documents */}
                    <div className="space-y-8 border-t border-gray-100 pt-8">
                      {/* Benefits */}
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-extrabold text-base text-primary-dark flex items-center gap-2">
                            <ListOrdered className="w-5 h-5 text-secondary" />
                            Key Benefits (Marathi)
                          </h4>
                          <button
                            onClick={() => addListItem(activeTab, 'benefitsMr')}
                            className="flex items-center gap-1 text-xs font-bold text-secondary hover:text-secondary-dark transition-colors px-3 py-1.5 bg-secondary/5 hover:bg-secondary/10 rounded-xl"
                          >
                            <Plus className="w-4 h-4" /> Add Benefit
                          </button>
                        </div>
                        <div className="space-y-3">
                          {currentLoan?.benefitsMr?.map((benefit, i) => (
                            <div key={i} className="flex gap-3 items-center">
                              <span className="w-6 h-6 rounded-full bg-emerald-50 text-emerald-600 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                {i + 1}
                              </span>
                              <input
                                type="text"
                                value={benefit}
                                onChange={(e) => handleListItemChange(activeTab, 'benefitsMr', i, e.target.value)}
                                className="w-full bg-gray-50 border border-gray-200 px-4 py-2.5 rounded-xl focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary/20 text-sm font-medium text-gray-700"
                              />
                              <button
                                onClick={() => removeListItem(activeTab, 'benefitsMr', i)}
                                className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                              >
                                <Trash2 className="w-4.5 h-4.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Eligibility */}
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-extrabold text-base text-primary-dark flex items-center gap-2">
                            <ListOrdered className="w-5 h-5 text-secondary" />
                            Eligibility Criteria (Marathi)
                          </h4>
                          <button
                            onClick={() => addListItem(activeTab, 'eligibilityMr')}
                            className="flex items-center gap-1 text-xs font-bold text-secondary hover:text-secondary-dark transition-colors px-3 py-1.5 bg-secondary/5 hover:bg-secondary/10 rounded-xl"
                          >
                            <Plus className="w-4 h-4" /> Add Criteria
                          </button>
                        </div>
                        <div className="space-y-3">
                          {currentLoan?.eligibilityMr?.map((item, i) => (
                            <div key={i} className="flex gap-3 items-center">
                              <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                {i + 1}
                              </span>
                              <input
                                type="text"
                                value={item}
                                onChange={(e) => handleListItemChange(activeTab, 'eligibilityMr', i, e.target.value)}
                                className="w-full bg-gray-50 border border-gray-200 px-4 py-2.5 rounded-xl focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary/20 text-sm font-medium text-gray-700"
                              />
                              <button
                                onClick={() => removeListItem(activeTab, 'eligibilityMr', i)}
                                className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                              >
                                <Trash2 className="w-4.5 h-4.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Loan File Order (Marathi) */}
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-extrabold text-base text-primary-dark flex items-center gap-2">
                            <ListOrdered className="w-5 h-5 text-secondary" />
                            Loan File Documents Serial Order (मराठी)
                          </h4>
                          <button
                            onClick={() => addListItem(activeTab, 'loanFileOrderMr')}
                            className="flex items-center gap-1 text-xs font-bold text-secondary hover:text-secondary-dark transition-colors px-3 py-1.5 bg-secondary/5 hover:bg-secondary/10 rounded-xl"
                          >
                            <Plus className="w-4 h-4" /> Add Document
                          </button>
                        </div>
                        <div className="space-y-3">
                          {currentLoan?.loanFileOrderMr?.map((item, i) => (
                            <div key={i} className="flex gap-3 items-center">
                              <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                {i + 1}
                              </span>
                              <input
                                type="text"
                                value={item}
                                onChange={(e) => handleListItemChange(activeTab, 'loanFileOrderMr', i, e.target.value)}
                                className="w-full bg-gray-50 border border-gray-200 px-4 py-2.5 rounded-xl focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary/20 text-sm font-medium text-gray-700"
                              />
                              <button
                                onClick={() => removeListItem(activeTab, 'loanFileOrderMr', i)}
                                className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                              >
                                <Trash2 className="w-4.5 h-4.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Guarantor Documents (Marathi) */}
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-extrabold text-base text-primary-dark flex items-center gap-2">
                            <ListOrdered className="w-5 h-5 text-secondary" />
                            Guarantor Documents (मराठी)
                          </h4>
                          <button
                            onClick={() => addListItem(activeTab, 'guarantorDocsMr')}
                            className="flex items-center gap-1 text-xs font-bold text-secondary hover:text-secondary-dark transition-colors px-3 py-1.5 bg-secondary/5 hover:bg-secondary/10 rounded-xl"
                          >
                            <Plus className="w-4 h-4" /> Add Document
                          </button>
                        </div>
                        <div className="space-y-3">
                          {currentLoan?.guarantorDocsMr?.map((item, i) => (
                            <div key={i} className="flex gap-3 items-center">
                              <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                {i + 1}
                              </span>
                              <input
                                type="text"
                                value={item}
                                onChange={(e) => handleListItemChange(activeTab, 'guarantorDocsMr', i, e.target.value)}
                                className="w-full bg-gray-50 border border-gray-200 px-4 py-2.5 rounded-xl focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary/20 text-sm font-medium text-gray-700"
                              />
                              <button
                                onClick={() => removeListItem(activeTab, 'guarantorDocsMr', i)}
                                className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                              >
                                <Trash2 className="w-4.5 h-4.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Medium Term Docs (Marathi) */}
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-extrabold text-base text-primary-dark flex items-center gap-2">
                            <ListOrdered className="w-5 h-5 text-secondary" />
                            Documents Required for Medium Term Loan (मराठी)
                          </h4>
                          <button
                            onClick={() => addListItem(activeTab, 'mediumTermDocsMr')}
                            className="flex items-center gap-1 text-xs font-bold text-secondary hover:text-secondary-dark transition-colors px-3 py-1.5 bg-secondary/5 hover:bg-secondary/10 rounded-xl"
                          >
                            <Plus className="w-4 h-4" /> Add Document
                          </button>
                        </div>
                        <div className="space-y-3">
                          {currentLoan?.mediumTermDocsMr?.map((item, i) => (
                            <div key={i} className="flex gap-3 items-center">
                              <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                {i + 1}
                              </span>
                              <input
                                type="text"
                                value={item}
                                onChange={(e) => handleListItemChange(activeTab, 'mediumTermDocsMr', i, e.target.value)}
                                className="w-full bg-gray-50 border border-gray-200 px-4 py-2.5 rounded-xl focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary/20 text-sm font-medium text-gray-700"
                              />
                              <button
                                onClick={() => removeListItem(activeTab, 'mediumTermDocsMr', i)}
                                className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                              >
                                <Trash2 className="w-4.5 h-4.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Site Inspection Report (Marathi) */}
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-extrabold text-base text-primary-dark flex items-center gap-2">
                            <ListOrdered className="w-5 h-5 text-secondary" />
                            Branch Officer Site Inspection Report (मराठी)
                          </h4>
                          <button
                            onClick={() => addListItem(activeTab, 'siteInspectionDocsMr')}
                            className="flex items-center gap-1 text-xs font-bold text-secondary hover:text-secondary-dark transition-colors px-3 py-1.5 bg-secondary/5 hover:bg-secondary/10 rounded-xl"
                          >
                            <Plus className="w-4 h-4" /> Add Document
                          </button>
                        </div>
                        <div className="space-y-3">
                          {currentLoan?.siteInspectionDocsMr?.map((item, i) => (
                            <div key={i} className="flex gap-3 items-center">
                              <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                {i + 1}
                              </span>
                              <input
                                type="text"
                                value={item}
                                onChange={(e) => handleListItemChange(activeTab, 'siteInspectionDocsMr', i, e.target.value)}
                                className="w-full bg-gray-50 border border-gray-200 px-4 py-2.5 rounded-xl focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary/20 text-sm font-medium text-gray-700"
                              />
                              <button
                                onClick={() => removeListItem(activeTab, 'siteInspectionDocsMr', i)}
                                className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                              >
                                <Trash2 className="w-4.5 h-4.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Documents Required */}
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-extrabold text-base text-primary-dark flex items-center gap-2">
                            <ListOrdered className="w-5 h-5 text-secondary" />
                            Documents Required (Marathi)
                          </h4>
                          <button
                            onClick={() => addListItem(activeTab, 'documentsMr')}
                            className="flex items-center gap-1 text-xs font-bold text-secondary hover:text-secondary-dark transition-colors px-3 py-1.5 bg-secondary/5 hover:bg-secondary/10 rounded-xl"
                          >
                            <Plus className="w-4 h-4" /> Add Document
                          </button>
                        </div>
                        <div className="space-y-3">
                          {currentLoan?.documentsMr?.map((docItem, i) => (
                            <div key={i} className="flex gap-3 items-center">
                              <span className="w-6 h-6 rounded-full bg-purple-50 text-purple-600 font-bold text-xs flex items-center justify-center flex-shrink-0">
                                {i + 1}
                              </span>
                              <input
                                type="text"
                                value={docItem}
                                onChange={(e) => handleListItemChange(activeTab, 'documentsMr', i, e.target.value)}
                                className="w-full bg-gray-50 border border-gray-200 px-4 py-2.5 rounded-xl focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary/20 text-sm font-medium text-gray-700"
                              />
                              <button
                                onClick={() => removeListItem(activeTab, 'documentsMr', i)}
                                className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                              >
                                <Trash2 className="w-4.5 h-4.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoanManager;
